import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { BadgeCheck, Download, Trash2, Shield, Flag, Clock, Pencil, ArrowRightLeft, Search, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Review Masuk — moderasi post-publikasi.
 * Semua state berada di <Admin />; komponen ini menerima props read-only + handler.
 */
export default function ReviewsTab({
  reviews,
  isAdminOrSuper,
  reviewVerFilter,
  setReviewVerFilter,
  reviewRoleFilter,
  setReviewRoleFilter,
  reviewGradMin,
  setReviewGradMin,
  reviewGradMax,
  setReviewGradMax,
  reviewEliteOnly,
  setReviewEliteOnly,
  loadReviews,
  downloadReviewsCsv,
  verifyReview,
  deleteReview,
}) {
  // Admin text-edit modal — star ratings intentionally NOT editable here (per spec).
  const [editingReview, setEditingReview] = useState(null);
  const [editForm, setEditForm] = useState({ body: "", pros: "", cons: "" });
  const [saving, setSaving] = useState(false);

  // "Pindahkan ke Sekolah" modal state — one review at a time. `moving` is the
  // review being moved; `moveQuery` is the debounced search input; `moveResults`
  // is the list from `/schools?q=`; `movePicked` is the selected target school
  // (confirmation step); `moveSaving` locks the confirm button during the PATCH.
  const [moving, setMoving] = useState(null);
  const [moveQuery, setMoveQuery] = useState("");
  const [moveResults, setMoveResults] = useState([]);
  const [movePicked, setMovePicked] = useState(null);
  const [moveSaving, setMoveSaving] = useState(false);
  const [moveLoading, setMoveLoading] = useState(false);

  const openMove = (r) => {
    setMoving(r);
    setMoveQuery("");
    setMoveResults([]);
    setMovePicked(null);
  };
  const closeMove = () => {
    if (moveSaving) return;
    setMoving(null); setMoveQuery(""); setMoveResults([]); setMovePicked(null);
  };

  // Debounced school search. Only fires when the modal is open and query >= 2 chars —
  // matches the public directory's UX and keeps admin usage cheap.
  useEffect(() => {
    if (!moving) return;
    const q = moveQuery.trim();
    if (q.length < 2) { setMoveResults([]); return; }
    setMoveLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get("/schools", { params: { q, status: "approved" } });
        // Exclude the review's current school from the picker so admin can't "move to same".
        const filtered = (res.data || []).filter((s) => s.school_id !== moving.school_id);
        setMoveResults(filtered.slice(0, 20));
      } catch {
        setMoveResults([]);
      } finally {
        setMoveLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [moveQuery, moving]);

  const confirmMove = async () => {
    if (!moving || !movePicked) return;
    setMoveSaving(true);
    try {
      await api.patch(`/admin/reviews/${moving.review_id}/move-school`, { new_school_id: movePicked.school_id });
      toast.success(`Ulasan dipindahkan ke ${movePicked.name}`);
      setMoving(null); setMovePicked(null); setMoveQuery(""); setMoveResults([]);
      loadReviews();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memindahkan ulasan");
    } finally {
      setMoveSaving(false);
    }
  };

  const openEdit = (r) => {
    setEditingReview(r);
    setEditForm({
      body: r.body || "",
      pros: r.pros || "",
      cons: r.cons || "",
    });
  };
  const closeEdit = () => { setEditingReview(null); setSaving(false); };
  const submitEdit = async () => {
    if (!editingReview) return;
    if (!editForm.body.trim()) { toast.error("Isi review tidak boleh kosong"); return; }
    setSaving(true);
    try {
      await api.patch(`/admin/reviews/${editingReview.review_id}/text-edit`, editForm);
      toast.success("Review berhasil diperbarui");
      closeEdit();
      loadReviews();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan perubahan");
      setSaving(false);
    }
  };

  return (
    <TabsContent value="reviews-mgmt" className="mt-6 space-y-3" data-testid="reviews-mgmt-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm text-[#4B5563]">
            Review terbaru — semuanya <strong>langsung tayang</strong> (post-moderation). Verifikasi review yang layak atau hapus yang melanggar.
          </p>
          {isAdminOrSuper && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-widest text-[#4B5563]">Verifikasi:</span>
              <Select value={reviewVerFilter} onValueChange={(v) => { setReviewVerFilter(v); loadReviews(v); }}>
                <SelectTrigger data-testid="reviews-verified-filter" className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua</SelectItem>
                  <SelectItem value="true">Sudah diverifikasi</SelectItem>
                  <SelectItem value="false">Belum diverifikasi</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-[10px] uppercase tracking-widest text-[#4B5563] ml-2">Peran:</span>
              <Select value={reviewRoleFilter} onValueChange={(v) => { setReviewRoleFilter(v); loadReviews(reviewVerFilter, v, reviewEliteOnly, reviewGradMin, reviewGradMax); }}>
                <SelectTrigger data-testid="reviews-role-filter" className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua peran</SelectItem>
                  <SelectItem value="parent">Orang Tua</SelectItem>
                  <SelectItem value="student">Siswa Aktif</SelectItem>
                  <SelectItem value="alumni">Alumni</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-[10px] uppercase tracking-widest text-[#4B5563] ml-2">Tahun lulus:</span>
              <Input type="number" placeholder="Min" value={reviewGradMin} onChange={(e) => setReviewGradMin(e.target.value)} onBlur={() => loadReviews()} data-testid="reviews-grad-min" className="w-20 h-8 text-xs" />
              <Input type="number" placeholder="Max" value={reviewGradMax} onChange={(e) => setReviewGradMax(e.target.value)} onBlur={() => loadReviews()} data-testid="reviews-grad-max" className="w-20 h-8 text-xs" />
              <label className="ml-2 inline-flex items-center gap-1.5 text-xs text-[#7C2D12] font-medium cursor-pointer">
                <input type="checkbox" checked={reviewEliteOnly}
                  onChange={(e) => { setReviewEliteOnly(e.target.checked); loadReviews(reviewVerFilter, reviewRoleFilter, e.target.checked, reviewGradMin, reviewGradMax); }}
                  data-testid="reviews-elite-only" className="accent-[#E07A5F]" />
                ⭐ Elite Alumni saja
              </label>
            </div>
          )}
        </div>
        {isAdminOrSuper && (
          <button onClick={downloadReviewsCsv} data-testid="download-reviews-csv" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-sm">
            <Download size={14} /> Unduh CSV lengkap
          </button>
        )}
      </div>
      {reviews.length === 0 && <div className="text-center text-[#4B5563] p-6" data-testid="reviews-empty-admin">Tidak ada review pada filter ini.</div>}
      {reviews.map((r, i) => (
        <div key={r.review_id} data-testid={`review-row-${i}`} className="bg-white border border-[#EAE7E0] rounded-2xl p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="text-xs uppercase tracking-widest text-[#4B5563] flex items-center gap-2 flex-wrap">
                {r.school_name} · <span className="font-semibold text-[#1F2937] normal-case tracking-normal">{r.user_name}</span> · {new Date(r.created_at).toLocaleDateString("id-ID")}
                {r.is_verified ? (
                  <span data-testid={`review-team-verified-flag-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#ECFDF5] text-[#065F46] text-[10px] uppercase tracking-wider border border-[#A7F3D0]">
                    <BadgeCheck size={10} /> Diverifikasi tim
                  </span>
                ) : (
                  <span data-testid={`review-team-pending-flag-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] text-[10px] uppercase tracking-wider border border-[#FCD34D]">
                    <Clock size={10} /> Belum diverifikasi
                  </span>
                )}
                {r.report_count > 0 && (
                  <span data-testid={`review-reports-flag-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FEE2E2] text-[#B91C1C] text-[10px] uppercase tracking-wider border border-[#FECACA]">
                    <Flag size={10} /> {r.report_count} laporan
                  </span>
                )}
                {r.is_anonymous && (
                  <span data-testid={`review-anon-flag-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#1E3A8A] text-[10px] uppercase tracking-wider border border-[#DBEAFE]">
                    <Shield size={10} /> Anonim di publik
                  </span>
                )}
                {r.verified_parent && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#DCFCE7] text-[#166534] text-[10px] uppercase tracking-wider border border-[#BBF7D0]">
                    <BadgeCheck size={10} /> Terverifikasi
                  </span>
                )}
                {r.reviewer_role === "student" && (
                  <span data-testid={`admin-review-role-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#ECFDF5] text-[#065F46] text-[10px] uppercase tracking-wider border border-[#A7F3D0]">
                    Siswa Aktif
                  </span>
                )}
                {r.reviewer_role === "alumni" && (
                  <span data-testid={`admin-review-role-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FEF3E7] text-[#7C2D12] text-[10px] uppercase tracking-wider border border-[#FED7AA]">
                    Alumni · {r.graduation_year || "—"}
                  </span>
                )}
                {r.is_elite_alumni && (
                  <span data-testid={`admin-review-elite-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#78350F] text-[10px] uppercase tracking-wider border border-[#FCD34D] font-semibold">
                    ⭐ Elite · {r.elite_university}
                  </span>
                )}
              </div>
              <div className="font-serif-display text-base text-[#1F2937] mt-1">{r.title}</div>
              <p className="mt-1 text-sm text-[#4B5563] line-clamp-2">{r.body}</p>
              {r.reviewer_role === "alumni" && (r.alumni_next_study || r.alumni_current_profession) && (
                <div className="mt-1.5 text-[11px] text-[#4B5563] flex flex-wrap gap-2" data-testid={`admin-alumni-outcome-${i}`}>
                  {r.alumni_next_study && <span>🎓 Studi: <b className="text-[#1F2937]">{r.alumni_next_study}</b></span>}
                  {r.alumni_current_profession && <span>💼 Profesi: <b className="text-[#1F2937]">{r.alumni_current_profession}</b></span>}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-1.5 shrink-0">
              {!r.is_verified && (
                <Button onClick={() => verifyReview(r.review_id, true)} data-testid={`verify-review-${i}`} className="rounded-full bg-[#065F46] hover:bg-[#047857] text-white h-9 px-3">
                  <BadgeCheck size={14} className="mr-1" /> Verifikasi
                </Button>
              )}
              {r.is_verified && (
                <Button variant="outline" onClick={() => verifyReview(r.review_id, false)} data-testid={`unverify-review-${i}`} className="rounded-full h-9 px-3 border-[#EAE7E0]">
                  Batalkan verifikasi
                </Button>
              )}
              <Button variant="outline" onClick={() => openEdit(r)} data-testid={`edit-review-${i}`} className="rounded-full border-[#EAE7E0] text-[#1E3A8A] h-9 px-3">
                <Pencil size={14} className="mr-1" /> Edit
              </Button>
              <Button variant="outline" onClick={() => openMove(r)} data-testid={`move-review-${i}`} className="rounded-full border-[#EAE7E0] text-[#7C2D12] h-9 px-3">
                <ArrowRightLeft size={14} className="mr-1" /> Pindahkan ke Sekolah
              </Button>
              <Button variant="outline" onClick={() => deleteReview(r.review_id)} data-testid={`delete-review-${i}`} className="rounded-full border-[#EAE7E0] text-[#B91C1C] h-9 px-3">
                <Trash2 size={14} className="mr-1" /> Hapus
              </Button>
            </div>
          </div>
        </div>
      ))}

      {/* ── Admin Edit Review modal — text-only per spec (skor bintang tidak diubah) ── */}
      <Dialog open={!!editingReview} onOpenChange={(v) => { if (!v) closeEdit(); }}>
        <DialogContent data-testid="edit-review-modal" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit review</DialogTitle>
            <DialogDescription>
              Anda hanya dapat mengubah teks review. Skor bintang tetap seperti yang diberikan oleh reviewer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#4B5563] font-semibold">Isi review</label>
              <Textarea
                value={editForm.body}
                onChange={(e) => setEditForm({ ...editForm, body: e.target.value })}
                data-testid="edit-review-body"
                className="mt-1 min-h-[120px]"
                maxLength={5000}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#065F46] font-semibold">Kelebihan</label>
                <Textarea
                  value={editForm.pros}
                  onChange={(e) => setEditForm({ ...editForm, pros: e.target.value })}
                  data-testid="edit-review-pros"
                  className="mt-1 min-h-[80px]"
                  maxLength={2000}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#B91C1C] font-semibold">Perlu ditingkatkan</label>
                <Textarea
                  value={editForm.cons}
                  onChange={(e) => setEditForm({ ...editForm, cons: e.target.value })}
                  data-testid="edit-review-cons"
                  className="mt-1 min-h-[80px]"
                  maxLength={2000}
                />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={closeEdit} data-testid="edit-review-cancel" className="rounded-full">
              Batal
            </Button>
            <Button onClick={submitEdit} disabled={saving} data-testid="edit-review-save" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">
              {saving ? "Menyimpan..." : "Simpan perubahan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Pindahkan ke Sekolah modal — updates ONLY `school_id` on the review ── */}
      <Dialog open={!!moving} onOpenChange={(v) => { if (!v) closeMove(); }}>
        <DialogContent data-testid="move-review-modal" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Pindahkan ulasan ke sekolah lain</DialogTitle>
            <DialogDescription>
              Hanya field <code className="text-[11px] bg-[#F3F1ED] px-1 py-0.5 rounded">school_id</code> ulasan yang akan diperbarui.
              Skor bintang, teks, verifikasi, foto, dan identitas reviewer tidak berubah.
              Aksi ini akan tercatat di Log Aksi admin.
            </DialogDescription>
          </DialogHeader>
          {moving && (
            <div className="mt-2 space-y-3">
              <div className="text-xs bg-[#F3F1ED] rounded-lg p-2.5 border border-[#EAE7E0]">
                <div className="uppercase tracking-widest text-[10px] text-[#4B5563]">Ulasan saat ini</div>
                <div className="mt-0.5 font-semibold text-[#1F2937]" data-testid="move-review-current-school">
                  {moving.school_name || moving.school_id}
                </div>
                <div className="text-[#4B5563] mt-1 line-clamp-1">{moving.title}</div>
              </div>

              {!movePicked && (
                <>
                  <label className="text-[10px] uppercase tracking-widest text-[#4B5563] font-semibold">
                    Cari sekolah tujuan
                  </label>
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5563]" />
                    <Input
                      autoFocus
                      value={moveQuery}
                      onChange={(e) => setMoveQuery(e.target.value)}
                      placeholder="Ketik minimal 2 huruf nama sekolah…"
                      data-testid="move-review-search-input"
                      className="pl-9"
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto border border-[#EAE7E0] rounded-lg divide-y divide-[#EAE7E0]" data-testid="move-review-results">
                    {moveQuery.trim().length < 2 && (
                      <div className="p-3 text-xs text-[#4B5563]">Mulai mengetik untuk mencari…</div>
                    )}
                    {moveQuery.trim().length >= 2 && moveLoading && (
                      <div className="p-3 text-xs text-[#4B5563]" data-testid="move-review-loading">Mencari…</div>
                    )}
                    {moveQuery.trim().length >= 2 && !moveLoading && moveResults.length === 0 && (
                      <div className="p-3 text-xs text-[#4B5563]" data-testid="move-review-no-results">Tidak ada sekolah cocok.</div>
                    )}
                    {moveResults.map((s, idx) => (
                      <button
                        key={s.school_id}
                        type="button"
                        onClick={() => setMovePicked(s)}
                        data-testid={`move-review-result-${idx}`}
                        className="w-full text-left px-3 py-2 hover:bg-[#F3F1ED] transition-colors flex items-start gap-2"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-[#1F2937] truncate">{s.name}</div>
                          <div className="text-[11px] text-[#4B5563] truncate">{s.city || "—"} · {s.school_type || "—"}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}

              {movePicked && (
                <div className="rounded-lg border border-[#065F46] bg-[#ECFDF5] p-3" data-testid="move-review-confirm-panel">
                  <div className="uppercase tracking-widest text-[10px] text-[#065F46] font-semibold flex items-center gap-1">
                    <Check size={12} /> Sekolah tujuan
                  </div>
                  <div className="mt-1 font-semibold text-[#065F46]" data-testid="move-review-picked-name">{movePicked.name}</div>
                  <div className="text-[11px] text-[#065F46]/80">{movePicked.city || "—"} · {movePicked.school_type || "—"}</div>
                  <button
                    type="button"
                    onClick={() => setMovePicked(null)}
                    data-testid="move-review-change-pick"
                    className="mt-2 text-[11px] text-[#065F46] hover:underline"
                  >
                    ← Pilih sekolah lain
                  </button>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={closeMove} data-testid="move-review-cancel" className="rounded-full" disabled={moveSaving}>
              Batal
            </Button>
            <Button
              onClick={confirmMove}
              disabled={!movePicked || moveSaving}
              data-testid="move-review-confirm"
              className="rounded-full bg-[#7C2D12] hover:bg-[#9A3412] text-white disabled:opacity-50"
            >
              {moveSaving ? "Memindahkan…" : "Konfirmasi pindah"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TabsContent>
  );
}
